import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

/**
 * Reusable informative modal for displaying information to the user
 *
 * @param {boolean} isOpen - Controls modal visibility
 * @param {string} title - Modal title (required)
 * @param {string} message - Main message to display (required)
 * @param {string} details - Optional additional details
 * @param {Array<string>} items - Optional list of items to display (e.g., usernames)
 * @param {string} itemsLabel - Label for the items list (defaults to "Items:")
 * @param {string} closeLabel - Custom close button text (defaults to "Close")
 * @param {Function} onClose - Callback when close button is clicked or modal is closed (required)
 * @param {string} severity - Visual style: 'info', 'warning', 'error' (defaults to 'info')
 * @param {boolean} showIcon - Whether to show the icon (defaults to true)
 */
function InformativeModal({
  isOpen,
  title,
  message,
  details,
  items = [],
  itemsLabel = 'Items:',
  closeLabel = 'Close',
  onClose,
  severity = 'info',
  showIcon = true
}) {
  // Severity configuration
  const severityConfig = {
    info: {
      borderColor: 'info.main',
      iconBg: 'info.lighter',
      iconColor: 'info.main',
      Icon: InfoOutlinedIcon
    },
    warning: {
      borderColor: 'warning.main',
      iconBg: 'warning.lighter',
      iconColor: 'warning.main',
      Icon: WarningAmberIcon
    },
    error: {
      borderColor: 'error.main',
      iconBg: 'error.lighter',
      iconColor: 'error.main',
      Icon: ErrorOutlineIcon
    }
  };

  const config = severityConfig[severity] || severityConfig.info;
  const IconComponent = config.Icon;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden'
        }
      }}
    >
      {/* Subtle colored top border */}
      <Box
        sx={{
          height: 4,
          backgroundColor: config.borderColor
        }}
      />

      <DialogTitle
        sx={{
          pt: 3,
          pb: 2,
          px: 3,
          backgroundColor: 'background.paper'
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'flex-start'
          }}
        >
          {showIcon && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: config.iconBg,
                color: config.iconColor,
                flexShrink: 0
              }}
            >
              <IconComponent sx={{ fontSize: 28 }} />
            </Box>
          )}

          <Box sx={{ flex: 1, pt: showIcon ? 1.5 : 0 }}>
            {/* Main message */}
            {message && (
              <Typography
                variant="body1"
                sx={{
                  color: 'text.primary',
                  fontWeight: 500,
                  mb: details || items.length > 0 ? 1.5 : 0
                }}
              >
                {message}
              </Typography>
            )}

            {/* Additional details */}
            {details && (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  mb: items.length > 0 ? 1.5 : 0
                }}
              >
                {details}
              </Typography>
            )}

            {/* List of items */}
            {items.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 600,
                    mb: 1
                  }}
                >
                  {itemsLabel}
                </Typography>
                <Box
                  sx={{
                    maxHeight: 200,
                    overflowY: 'auto',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    backgroundColor: 'background.default'
                  }}
                >
                  <List dense disablePadding>
                    {items.map((item, index) => (
                      <React.Fragment key={index}>
                        {index > 0 && <Divider />}
                        <ListItem>
                          <ListItemText
                            primary={item}
                            primaryTypographyProps={{
                              variant: 'body2',
                              sx: { fontFamily: 'monospace' }
                            }}
                          />
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{ textTransform: 'none', minWidth: 100 }}
        >
          {closeLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default InformativeModal;